import React, { useEffect, useState } from "react";
import {
  getChanneldata,
  updateWhatsappSetting,
  sendWhatsAppChannel,
} from "../../Services/api";
import { useSelector } from "react-redux";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Badge,
  Spinner,
  Flex,
  useToast,
  Radio,
  IconButton,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  FormErrorMessage,
  Button,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { BiTrash, BiPencil, BiShow, BiHide } from "react-icons/bi";
import PrimaryButton from "../../components/themeComponents/PrimaryButton";
import Whatsapp from "./Whatsapp";
import axiosInstance from "../../Services/axiosInstance";
import Swal from "sweetalert2";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { whatsappchannelSchema } from "../../_helpers/validationSchema";
import componentMapping from "./ComponentMapping";

export default function WhatsappSettingList() {
  const orgToken = useSelector((state) => state.auth.selectedOrg?.token);
  const [channelData, setChannelData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const toast = useToast();

  const fetchChannelsData = async () => {
    if (!orgToken) return;
    setLoading(true);
    try {
      const res = await getChanneldata(orgToken);
      if (res?.data?.status === "success") {
        const data = res.data.data.response;
        const list = Array.isArray(data) ? data : data ? [data] : [];
        setChannelData(list);
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to load channels",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChannelsData();
  }, [orgToken]);

  const deleteWhatsappSetting = async (uuid) => {
    const result = await Swal.fire({
      title: "Delete Channel?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/whatsapp/setting/${uuid}`);
        toast({
          title: "Deleted",
          description: "WhatsApp channel removed successfully",
          status: "success",
        });
        fetchChannelsData();
      } catch (err) {
        toast({
          title: "Error",
          description: err.response?.data?.message || "Delete failed",
          status: "error",
        });
      }
    }
  };

  const handleSetDefault = async (uuid) => {
    const result = await Swal.fire({
      title: "Set as Default?",
      text: "This will become your primary WhatsApp channel",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, set default",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        // Adjust endpoint based on your actual API
        await axiosInstance.patch(`/whatsapp/setting/${uuid}/default`);
        // Alternative: if it uses PUT with payload
        // await axiosInstance.put(`/whatsapp/setting/${uuid}`, { isDefault: true });

        toast({
          title: "Success",
          description: "Default channel updated",
          status: "success",
        });
        fetchChannelsData();
      } catch (err) {
        toast({
          title: "Failed",
          description: err.response?.data?.message || "Could not set default",
          status: "error",
        });
      }
    }
  };

  const openEditModal = (channel) => {
    
    setEditingChannel(channel);
    setShowAuth(false);
    setEditModalOpen(true);
  };

  const handleUpdate = async (values, { setSubmitting }) => {
  
    if (!editingChannel?.uuid) {
      toast({
        title: "Error",
        description: "Channel ID is missing",
        status: "error",
      });
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
    "name": values.name,
    "businessPhone": values.businessPhone,
    "provider": editingChannel.provider,
    "account": values.account,
    "auth": values.auth,
    "org_id": orgToken,
    "org_token": orgToken,
    "remarks": values.remarks
}

      // Use PUT for update
      await updateWhatsappSetting(payload,editingChannel.id)

      toast({
        title: "Updated!",
        description: "WhatsApp channel updated successfully",
        status: "success",
      });

      setEditModalOpen(false);
      fetchChannelsData();
    } catch (err) {
      const message = err.response?.data?.message || "Update failed";
      toast({
        title: "Error",
        description: message,
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getInitialValues = (channel) => ({
    name: channel.name || "",
    businessPhone: channel.business_phone || "",
    account: channel.account || "",
    auth: channel.auth || "",
    remarks: channel.remarks || "",
    // Add any provider-specific fields here if they are common
  });

  return (
    <>
      <PrimaryButton
        label="Add Provider +"
        float="right"
        onClick={onOpen}
        mb={6}
      />

      {loading ? (
        <Flex justify="center" align="center" h="300px">
          <Spinner size="xl" />
        </Flex>
      ) : (
        <Table variant="simple" bg="#fff" borderRadius="lg" boxShadow="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>Name</Th>
              <Th>Provider</Th>
              <Th>Default</Th>
              <Th textAlign="center">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {channelData.length === 0 ? (
              <Tr>
                <Td colSpan={4} textAlign="center" color="gray.500" py={10}>
                  No WhatsApp channels configured yet
                </Td>
              </Tr>
            ) : (
              channelData.map((item) => (
                <Tr key={item.uuid} _hover={{ bg: "gray.50" }}>
                  <Td fontWeight="medium">{item.name || "—"}</Td>
                  <Td>
                    <Badge colorScheme="purple" variant="solid">
                      {item.provider}
                    </Badge>
                  </Td>
                  <Td>
                    <Radio
                      isChecked={!!item.isDefault}
                      onChange={() => handleSetDefault(item.uuid)}
                      colorScheme="green"
                    />
                  </Td>
                  <Td>
                    <HStack spacing={4} justify="center">
                      <IconButton
                        aria-label="Edit"
                        icon={<BiPencil />}
                        size="sm"
                        colorScheme="blue"
                        variant="ghost"
                        onClick={() => openEditModal(item)}
                      />
                      <IconButton
                        aria-label="Delete"
                        icon={<BiTrash />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => deleteWhatsappSetting(item.uuid)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      )}

      {/* Add New Provider Modal */}
      <Modal size="4xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody py={6}>
            <Whatsapp refreshList={fetchChannelsData} onclose={onClose} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal size="xl" isOpen={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Channel: {editingChannel?.name}</ModalHeader>
          <ModalCloseButton />

          {editingChannel && (
            <Formik
              initialValues={getInitialValues(editingChannel)}
              validationSchema={
                whatsappchannelSchema[editingChannel?.provider?.toLowerCase()]
              }
              onSubmit={handleUpdate}
              enableReinitialize
            >
              {({ values, isSubmitting }) => {
                const ProviderFields = componentMapping[editingChannel.provider];

                return (
                  <Form>
                    <ModalBody>
                      <VStack spacing={5} align="stretch">
                        <FormControl isRequired>
                          <FormLabel>Name</FormLabel>
                          <Field as={Input} name="name" placeholder="Channel name" />
                          <ErrorMessage name="name" component={FormErrorMessage} />
                        </FormControl>

                        <FormControl>
                          <FormLabel>Provider</FormLabel>
                          <Input
                            value={editingChannel.provider}
                            isReadOnly
                            bg="gray.100"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel>Business Phone</FormLabel>
                          <Field
                            as={Input}
                            name="businessPhone"
                            placeholder="e.g. +1234567890"
                          />
                          <ErrorMessage
                            name="businessPhone"
                            component={FormErrorMessage}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel>Account</FormLabel>
                          <Field as={Input} name="account" />
                          <ErrorMessage name="account" component={FormErrorMessage} />
                        </FormControl>

                        {/* Auth Token with Eye Toggle */}
                        <FormControl>
                          <FormLabel>Auth Token</FormLabel>
                          <InputGroup>
                            <Field
                              as={Input}
                              name="auth"
                              type={showAuth ? "text" : "password"}
                              autoComplete="off"
                              pr="4.5rem"
                            />
                            <InputRightElement width="4.5rem">
                              <Button
                                h="1.75rem"
                                size="sm"
                                onClick={() => setShowAuth(!showAuth)}
                                variant="ghost"
                              >
                                {showAuth ? <BiHide /> : <BiShow />}
                              </Button>
                            </InputRightElement>
                          </InputGroup>
                          <ErrorMessage name="auth" component={FormErrorMessage} />
                        </FormControl>

                        <FormControl>
                          <FormLabel>Remarks</FormLabel>
                          <Field as={Textarea} name="remarks" rows={3} />
                          <ErrorMessage name="remarks" component={FormErrorMessage} />
                        </FormControl>

                        {/* Render Provider-Specific Fields */}
                        {ProviderFields && <ProviderFields formikProps={{ values }} />}
                      </VStack>
                    </ModalBody>

                    <ModalFooter>
                      <PrimaryButton
                        label="Update Channel"
                        type="submit"
                        isLoading={isSubmitting}
                        mr={3}
                      />
                      <Button
                        variant="ghost"
                        onClick={() => setEditModalOpen(false)}
                      >
                        Cancel
                      </Button>
                    </ModalFooter>
                  </Form>
                );
              }}
            </Formik>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}